import { logDebug } from "./log";
/**
 * Google Analytics 4 (gtag.js) — client-side page-view tracking.
 *
 * Activates only when REACT_APP_GA_MEASUREMENT_ID is set in frontend/.env at
 * build time. When unset, all functions become safe no-ops so preview keeps
 * working and no ghost network requests get sent.
 *
 * Docs: https://developers.google.com/analytics/devguides/collection/ga4
 */

const MEASUREMENT_ID = (
  typeof process !== "undefined" && process.env && process.env.REACT_APP_GA_MEASUREMENT_ID
) || "";

let initialized = false;

// Inject the gtag.js snippet exactly once.
function ensureLoaded() {
  if (typeof window === "undefined" || !MEASUREMENT_ID) return;
  if (initialized) return;
  initialized = true;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  // Disable automatic page_view so SPA route changes control it explicitly.
  gtag("config", MEASUREMENT_ID, { send_page_view: false });
}

// --- Public API ---
export const isGAActive = () => Boolean(MEASUREMENT_ID);

// Fire a page_view on every SPA route change.
export const trackGAPageView = (path) => {
  try {
    if (!MEASUREMENT_ID) return;
    ensureLoaded();
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: path || window.location.pathname,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  } catch (error) { logDebug("GA pageview suppressed:", error?.message); }
};

// Generic event helper.
export const trackGAEvent = (name, params) => {
  try {
    if (!MEASUREMENT_ID) return;
    ensureLoaded();
    if (typeof window.gtag === "function") {
      window.gtag("event", name, params || {});
    }
  } catch (error) { logDebug("GA event suppressed:", error?.message); }
};

// GA4 standard ecommerce purchase event — fired on the $2 lifetime unlock.
export const trackGAPurchase = () =>
  trackGAEvent("purchase", {
    transaction_id: `unlock_${Date.now()}`,
    currency: "AUD",
    value: 2,
    items: [{ item_id: "unlock_lifetime", item_name: "Fetchright Unlock", price: 2, quantity: 1 }],
  });

// Fired when a visitor finishes the 15-question quiz.
export const trackGAQuizComplete = () =>
  trackGAEvent("quiz_complete", { content_name: "breed_quiz" });

// Fired when a visitor starts Stripe checkout for the $2 unlock.
export const trackGABeginCheckout = () =>
  trackGAEvent("begin_checkout", {
    currency: "AUD",
    value: 2,
    items: [{ item_id: "unlock_lifetime", item_name: "Fetchright Unlock", price: 2, quantity: 1 }],
  });

// Fired when a visitor shares their results. method = native | copy | facebook
export const trackGAShare = (method) =>
  trackGAEvent("share", { method: method || "unknown", content_type: "quiz_results" });

// Fired when a visitor saves their quiz results.
export const trackGASaveResults = () =>
  trackGAEvent("save_results", { content_type: "quiz_results" });

// Fired when a visitor clicks an outbound affiliate link (insurance, kit, vet).
export const trackGAAffiliateClick = (partner, slot) =>
  trackGAEvent("affiliate_click", { partner: partner || "unknown", slot: slot || "unknown" });
